import { TFunction } from "i18next";
import { AbsenceType } from "model/Schedule";
import { TimeReportResolution } from "model/TimeReport";

const absenceTypeTranslationKeyByType: Record<AbsenceType, string> = {
  [AbsenceType.SICK_LEAVE]: "Schedule.SickLeave",
  [AbsenceType.SICK_LEAVE_CHILD]: "Schedule.SickLeaveChild",
  [AbsenceType.VACATION]: "Schedule.Vacation",
  [AbsenceType.PARENTAL_LEAVE]: "Schedule.ParentalLeave",
  [AbsenceType.LEAVE_OF_ABSENCE]: "Schedule.LeaveOfAbsence",
  [AbsenceType.OTHER]: "Schedule.Other",
  [AbsenceType.CANCELLED_BY_EMPLOYER]: "Schedule.CancelledByEmployer",
};

const resolutionTranslationKeyByType: Partial<
  Record<TimeReportResolution, string>
> = {
  [TimeReportResolution.SICKLEAVE]: "Schedule.SickLeave",
  [TimeReportResolution.SICKLEAVECHILD]: "Schedule.SickLeaveChild",
  [TimeReportResolution.VACATION]: "Schedule.Vacation",
  [TimeReportResolution.PARENTALLEAVE]: "Schedule.ParentalLeave",
  [TimeReportResolution.LEAVEOFABSENCE]: "Schedule.LeaveOfAbsence",
  [TimeReportResolution.CANCELLEDBYEMPLOYER]: "Schedule.CancelledByEmployer",
  [TimeReportResolution.FUNERALLEAVE]: "Schedule.Other",
  [TimeReportResolution.STUDYLEAVE]: "Schedule.Other",
  [TimeReportResolution.LEAVEUNIONASSIGNEMENTS]: "Schedule.Other",
  [TimeReportResolution.MILITARYSERVICE]: "Schedule.Other",
  [TimeReportResolution.OTHER]: "Schedule.Other",
};

const timeReportAbsenceResolutions = new Set<TimeReportResolution>([
  TimeReportResolution.SICKLEAVE,
  TimeReportResolution.SICKLEAVECHILD,
  TimeReportResolution.VACATION,
  TimeReportResolution.PARENTALLEAVE,
  TimeReportResolution.LEAVEOFABSENCE,
  TimeReportResolution.FUNERALLEAVE,
  TimeReportResolution.STUDYLEAVE,
  TimeReportResolution.LEAVEUNIONASSIGNEMENTS,
  TimeReportResolution.MILITARYSERVICE,
  TimeReportResolution.OTHER,
  TimeReportResolution.CANCELLEDBYEMPLOYER,
]);

/** Resolve a localized label for a specific absence type. */
export const getAbsenceTypeLabel = (
  type: AbsenceType,
  translate: TFunction,
): string => {
  return translate(absenceTypeTranslationKeyByType[type]);
};

/** Build selectable absence-type options with localized labels. */
export const getAbsenceTypeOptions = (
  translate: TFunction,
): Array<{ value: AbsenceType; label: string }> => {
  return [
    {
      value: AbsenceType.SICK_LEAVE,
      label: getAbsenceTypeLabel(AbsenceType.SICK_LEAVE, translate),
    },
    {
      value: AbsenceType.SICK_LEAVE_CHILD,
      label: getAbsenceTypeLabel(AbsenceType.SICK_LEAVE_CHILD, translate),
    },
    {
      value: AbsenceType.VACATION,
      label: getAbsenceTypeLabel(AbsenceType.VACATION, translate),
    },
    {
      value: AbsenceType.PARENTAL_LEAVE,
      label: getAbsenceTypeLabel(AbsenceType.PARENTAL_LEAVE, translate),
    },
    {
      value: AbsenceType.LEAVE_OF_ABSENCE,
      label: getAbsenceTypeLabel(AbsenceType.LEAVE_OF_ABSENCE, translate),
    },
    {
      value: AbsenceType.OTHER,
      label: getAbsenceTypeLabel(AbsenceType.OTHER, translate),
    },
    {
      value: AbsenceType.CANCELLED_BY_EMPLOYER,
      label: getAbsenceTypeLabel(AbsenceType.CANCELLED_BY_EMPLOYER, translate),
    },
  ];
};

/** Resolve whether a time-report resolution should be treated as absence. */
export const isTimeReportAbsenceResolution = (
  resolution: TimeReportResolution,
): boolean => {
  return timeReportAbsenceResolutions.has(resolution);
};

/** Resolve a localized label for a time-report absence resolution. */
export const getTimeReportAbsenceResolutionLabel = (
  resolution: TimeReportResolution,
  translate: TFunction,
): string => {
  const key = resolutionTranslationKeyByType[resolution] ?? "Schedule.Other";
  return translate(key);
};

/** Check whether a resolution is a supported absence type. */
export const isAbsenceType = (resolution: string): resolution is AbsenceType =>
  Object.values(AbsenceType).some((type) => type === resolution);
